import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Stethoscope, Plus } from 'lucide-react';
import { patientsApi } from '../lib/api';
import { format } from 'date-fns';

export default function PatientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: patient, isLoading } = useQuery({
    queryKey: ['patient', id],
    queryFn: () => patientsApi.get(id),
  });

  const { data: consultations } = useQuery({
    queryKey: ['patient-consultations', id],
    queryFn: () => patientsApi.consultations(id),
    enabled: !!id,
  });

  if (isLoading) return <div className="p-8 text-clinical-muted">Loading…</div>;
  if (!patient) return <div className="p-8 text-red-600">Patient not found.</div>;

  return (
    <div className="p-8 max-w-4xl">
      {/* Back */}
      <button onClick={() => navigate('/app/patients')} className="btn-ghost mb-6 -ml-2">
        <ArrowLeft className="w-4 h-4" />
        Back to Patients
      </button>

      {/* Patient header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            {patient.lastName}, {patient.firstName}
          </h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-clinical-muted">
            <span className="font-mono">#{patient.patientNumber}</span>
            {patient.dateOfBirth && <span>· DOB: {format(new Date(patient.dateOfBirth), 'dd MMM yyyy')}</span>}
            {patient.gender && <span>· {patient.gender}</span>}
          </div>
        </div>
        <button
          onClick={() => navigate(`/app/consultations/new?patientId=${patient.id}`)}
          className="btn-primary"
        >
          <Plus className="w-4 h-4" />
          New Consultation
        </button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Clinical info */}
        <div className="col-span-2 space-y-5">
          {/* Contact */}
          <div className="card p-5">
            <h2 className="font-semibold text-slate-800 mb-3">Contact Information</h2>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-clinical-muted">Phone</dt>
                <dd className="text-slate-800">{patient.phone || '—'}</dd>
              </div>
              <div>
                <dt className="text-clinical-muted">Email</dt>
                <dd className="text-slate-800">{patient.email || '—'}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-clinical-muted">Address</dt>
                <dd className="text-slate-800">{patient.address || '—'}</dd>
              </div>
            </dl>
          </div>

          {/* Medical info */}
          <div className="card p-5">
            <h2 className="font-semibold text-slate-800 mb-3">Medical Information</h2>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-clinical-muted">Blood Group</dt>
                <dd className="text-slate-800 font-mono">{patient.bloodGroup || '—'}</dd>
              </div>
              <div>
                <dt className="text-clinical-muted">Allergies</dt>
                <dd className="text-slate-800">
                  {patient.allergies?.length > 0
                    ? patient.allergies.map(a => <span key={a} className="badge-yellow mr-1">{a}</span>)
                    : 'None documented'}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-clinical-muted mb-1">Chronic Conditions</dt>
                <dd className="text-slate-800">
                  {patient.chronicConditions?.length > 0
                    ? patient.chronicConditions.map(c => <span key={c} className="badge-blue mr-1">{c}</span>)
                    : 'None documented'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Consultation history */}
          <div className="card p-5">
            <h2 className="font-semibold text-slate-800 mb-4">Consultation History</h2>
            {consultations?.length === 0 ? (
              <p className="text-sm text-clinical-muted">No consultations on record.</p>
            ) : (
              <div className="space-y-2">
                {consultations?.map(c => (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/app/consultations/${c.id}`)}
                    className="w-full text-left flex items-center justify-between p-3 rounded-lg border border-clinical-border hover:bg-slate-50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-800">{c.chief_complaint}</p>
                      <p className="text-xs text-clinical-muted mt-0.5">
                        {format(new Date(c.consultation_date), 'dd MMM yyyy · HH:mm')} · {c.clinician_name}
                      </p>
                    </div>
                    <span className={`badge ${c.status === 'completed' ? 'badge-green' : 'badge-yellow'}`}>
                      {c.status}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
